/**
 * The router. OWNER: web-frontend-engineer.
 *
 * Small on purpose. Three contracts it exists to keep, all from FLOWS_SPEC §2.8:
 *   · `KEY-ROUTE` — on every route change focus moves to the page `h1`
 *     (`tabindex="-1"`), the document title updates carrying NO body data
 *     ([T-33]), and the page name is announced once, politely.
 *   · Role guards — a client reaching `/atelier/*` gets `ST-403`, which is
 *     byte-identical to `ST-404`, so nothing leaks about what exists.
 *   · `[T-33]` — a route may carry an opaque id or an order reference and
 *     nothing else. There is no measurement, body descriptor or specification
 *     value in any path, query string or fragment in this table.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export interface RouteMatch {
  readonly path: string;
  readonly params: Readonly<Record<string, string>>;
  readonly query: URLSearchParams;
}

interface NavigationContextValue {
  readonly match: RouteMatch;
  readonly navigate: (to: string, options?: { replace?: boolean }) => void;
}

const NavigationContext = createContext<NavigationContextValue | null>(null);

function currentMatch(): RouteMatch {
  const url = new URL(window.location.href);
  return { path: url.pathname, params: {}, query: url.searchParams };
}

export function NavigationProvider({ children }: { children: ReactNode }) {
  const [match, setMatch] = useState<RouteMatch>(() => currentMatch());

  useEffect(() => {
    const onPop = () => setMatch(currentMatch());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    if (options?.replace === true) window.history.replaceState(null, '', to);
    else window.history.pushState(null, '', to);
    setMatch(currentMatch());
    // Navigation is instant; there is no screen transition (MOTION_SPEC §6.1).
    window.scrollTo(0, 0);
  }, []);

  const value = useMemo(() => ({ match, navigate }), [match, navigate]);
  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigation(): NavigationContextValue {
  const value = useContext(NavigationContext);
  if (value === null) throw new Error('useNavigation outside NavigationProvider');
  return value;
}

/** `/orders/:orderRef` against `/orders/PT-2026-0148` → `{ orderRef: 'PT-2026-0148' }`. */
export function matchPath(
  pattern: string,
  pathname: string,
): Readonly<Record<string, string>> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = pathname.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i += 1) {
    const p = patternParts[i] as string;
    const v = pathParts[i] as string;
    if (p.startsWith(':')) params[p.slice(1)] = decodeURIComponent(v);
    else if (p !== v) return null;
  }
  return params;
}
